import { createServerFn } from '@tanstack/react-start'
import { getSupabaseAdminClient } from '~/server/common/bindings'
import { getSpace, type SpaceKey } from '~/server/space/space-detail.service'
import { getOtherSpaces, type OtherSpace } from '~/server/space/other-spaces.service'
import type { SpaceDetail } from '~/shared/space/space-detail.types'

export type SpaceLoaderData = {
  space: SpaceDetail
  otherSpaces: OtherSpace[]
}

const CACHE_TTL_SECONDS = 300
const CACHE_VERSION = 'v2'

function buildCacheKey(spaceKey: SpaceKey): string {
  if ('spaceId' in spaceKey) return `https://space-cache/${CACHE_VERSION}/id/${spaceKey.spaceId}`
  return `https://space-cache/${CACHE_VERSION}/slug/${spaceKey.mainVenueSlug}/${spaceKey.spaceSlug}`
}

async function getCached(key: string): Promise<SpaceLoaderData | null> {
  if (typeof caches === 'undefined') return null
  try {
    const cache = await caches.open('space-cache')
    const res = await cache.match(new Request(key))
    if (!res) return null
    return res.json()
  } catch {
    return null
  }
}

async function setCached(key: string, data: SpaceLoaderData): Promise<void> {
  if (typeof caches === 'undefined') return
  try {
    const cache = await caches.open('space-cache')
    const res = new Response(JSON.stringify(data), {
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': `s-maxage=${CACHE_TTL_SECONDS}`,
      },
    })
    await cache.put(new Request(key), res)
  } catch {
    // caching is best-effort
  }
}

export const fetchSpaceData = createServerFn()
  .inputValidator((d: SpaceKey) => d)
  .handler(async ({ data: spaceKey }): Promise<SpaceLoaderData> => {
    const cacheKey = buildCacheKey(spaceKey)
    const cached = await getCached(cacheKey)
    if (cached) return cached

    const adminClient = getSupabaseAdminClient()

    const space = await getSpace(spaceKey, adminClient)

    const otherSpaces = await getOtherSpaces({ venueId: space.venue.id, spaceId: space.id }, adminClient)

    const result: SpaceLoaderData = { space, otherSpaces }

    void setCached(cacheKey, result)

    return result
  })
